// store/modules/notification.ts
import { Module } from 'vuex'

import { ElNotification } from 'element-plus'

import { ArenaModuleName, ConfigStateModuleName } from '../modules'

import { NotificationPositionType } from '@/domain/value_objects/notification/notificationPositionType'
import { NotificationType } from '@/domain/value_objects/notification/notificationType'

interface NotificationState {
  runningNotification: any | null
}

export const notificationModule = (): Module<NotificationState, any> => {
  return {
    namespaced: true,
    state: {
      runningNotification: null
    },
    mutations: {
      setRunningNotification(state, notification: ReturnType<typeof ElNotification>) {
        state.runningNotification = notification
      },

      clearRunningNotification(state) {
        if (state.runningNotification) {
          state.runningNotification.close()
          state.runningNotification = null
        }
      }
    },
    actions: {
      createNotification(
        { commit, rootGetters },
        {
          message,
          type,
          icon,
          position,
          duration
        }: {
          message: string
          type: NotificationType
          icon: string
          position: NotificationPositionType
          duration: number
        }
      ) {
        commit('clearRunningNotification')

        const isDarkMode = rootGetters[`${ConfigStateModuleName}/viewMode`] == 'DARK'
        const customClass = isDarkMode ? 'bg-notif-dark' : 'bg-notif'

        const notice = ElNotification({
          message,
          type,
          customClass,
          icon,
          dangerouslyUseHTMLString: true,
          position,
          duration
        })

        commit('setRunningNotification', notice)
        return notice
      },

      createFormattedNotification(
        { dispatch, rootGetters },
        {
          title,
          message = '',
          type,
          icon,
          position,
          duration,
          titleFontSize = '1rem',
          messageFontSize = '1rem'
        }: {
          title: string
          message: string
          type: NotificationType
          icon: string
          position: NotificationPositionType
          duration: number
          titleFontSize?: string
          messageFontSize?: string
        }
      ) {
        console.trace('[NOTIF] createFormattedNotification', title)
        const isDarkMode = rootGetters[`${ConfigStateModuleName}/viewMode`] == 'DARK'
        const titleClass = `tit-${type}${isDarkMode ? '-dark' : ''}`
        const messageClass = isDarkMode ? 'msg-dark' : 'msg'

        const formattedMessage = `
        <div class="${titleClass}" style="font-size:${titleFontSize};">${title}</div>
        ${message ? `<div class="${messageClass}" style="font-size:${messageFontSize};">${message}</div>` : ''}`

        return dispatch('createNotification', {
          message: formattedMessage,
          type,
          icon,
          position,
          duration
        })
      },

      async welcomeNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'AOJへようこそ' : 'Welcome to AOJ'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 1000
        })
      },

      async loginNotification({ dispatch, rootGetters }, { userId }: { userId: string }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const loginStr = isJapanese ? 'ログイン' : 'Logged in'
        const welcomeStr = isJapanese ? `ようこそ ${userId}` : `Welcome ${userId}`

        await dispatch('createFormattedNotification', {
          title: loginStr,
          message: welcomeStr,
          type: 'success',
          icon: 'icon-success',
          position: 'top-left',
          duration: 1000
        })
      },

      async logoutNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'ログアウトしました' : 'Logged out'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 2000
        })
      },

      async signupNotification({ dispatch, rootGetters }, { userId }: { userId: string }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'アカウントを作成しました' : 'Created an account'
        const welcomeStr = isJapanese ? `ようこそ ${userId}` : `Welcome ${userId}`

        await dispatch('createFormattedNotification', {
          title: str,
          message: welcomeStr,
          type: 'success',
          icon: 'icon-success',
          position: 'top-left',
          duration: 1000
        })
      },

      async errorNotification({ dispatch, rootGetters }, { error }: { error: string }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'エラー' : 'Error'

        await dispatch('createFormattedNotification', {
          title: str,
          message: error,
          type: 'error',
          icon: 'icon-error',
          position: 'top-left',
          duration: 2000
        })
      },

      async registerReviewNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'レビューをリクエストしました' : 'Requested review'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'success',
          icon: 'icon-success',
          position: 'top-left',
          duration: 2000
        })
      },

      async invalidReviewNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? '無効なレビューリクエストです' : 'Invalid review request'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'error',
          icon: 'icon-error',
          position: 'top-left',
          duration: 2000
        })
      },

      async registerReviewErrorNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'レビューリクエストエラー' : 'Review request error'
        const detailStr = isJapanese
          ? 'リクエストは拒否されました。以下の点をご確認ください。<br><br>・リクエスト数の超過<br>・短時間での大量のリクエスト<br>・システムのメンテナンスや不具合<br>・提出コードの公開ポリシーがprivate'
          : 'Request was denied for some reason. Please check the following.<br><br>- Excessive number of requests.<br>- Large number of requests in a short period of time.<br>- System maintenance or problem.<br>- The submission code policy is private'

        await dispatch('createFormattedNotification', {
          title: str,
          message: detailStr,
          type: 'error',
          icon: 'icon-error',
          position: 'top-left',
          duration: 0
        })
      },

      async evaluateReviewNotification({ dispatch, rootGetters }, { score }: { score: number }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'レビューの評価をしました' : 'Evaluate the review'
        const scoreLabel = isJapanese ? `スコア：` : `Score:`

        await dispatch('createFormattedNotification', {
          title: str,
          message: `${scoreLabel}${score}`,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 2000,
          titleFontSize: '1rem',
          messageFontSize: '1.125rem'
        })
      },

      async closeEntryNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'エントリーをクローズしました' : 'Entry closed'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 2000
        })
      },

      async submitReviewNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'レビューを投稿しました' : 'Submitted a review'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 2000
        })
      },

      async copyNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'コピーしました' : 'Copied'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-left',
          duration: 2000
        })
      },

      async acNotification(
        { dispatch, rootGetters },
        { type, problemId, judgeId }: { type: 'Run' | 'Judge'; problemId: string; judgeId: string }
      ) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const typeStr = isJapanese ? (type === 'Run' ? 'テスト' : 'ジャッジ') : type
        const arenaId = rootGetters[`${ArenaModuleName}/arenaId`]
        const arenaStr = arenaId == '' ? `` : `ArenaID: ${arenaId}<br>`

        await dispatch('createFormattedNotification', {
          title: 'ACCEPTED',
          message: `${arenaStr}${typeStr}: ${problemId}<br>JudgeID: ${judgeId}`,
          type: 'success',
          icon: 'icon-success',
          position: 'top-right',
          duration: 2300
        })
      },

      async wrongNotification(
        { dispatch, rootGetters },
        {
          status,
          type,
          problemId,
          judgeId
        }: {
          status: string
          type: 'Run' | 'Judge'
          problemId: string
          judgeId: string
        }
      ) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const typeStr = isJapanese ? (type === 'Run' ? 'テスト' : 'ジャッジ') : type
        const arenaId = rootGetters[`${ArenaModuleName}/arenaId`]
        const arenaStr = arenaId == '' ? '' : `ArenaID: ${arenaId}<br>`

        await dispatch('createFormattedNotification', {
          title: status,
          message: `${arenaStr}${typeStr}: ${problemId}<br>JudgeID: ${judgeId}`,
          type: 'error',
          icon: 'icon-error',
          position: 'top-right',
          duration: 2300
        })
      },

      async freeSpaceResultNotification(
        { dispatch, rootGetters },
        { showFreeSpaceModal, problemId }: { showFreeSpaceModal: boolean; problemId: string }
      ) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const typeStr = isJapanese ? 'テスト' : 'Test'
        const statusStr = isJapanese ? '実行終了' : 'Execution finished'
        const id = showFreeSpaceModal ? 'FreeSpace' : problemId

        await dispatch('createFormattedNotification', {
          title: statusStr,
          message: `${typeStr}: ${id}`,
          type: 'info',
          icon: 'icon-info',
          position: 'top-right',
          duration: 2300
        })
      },

      async openRunningNotification(
        { dispatch, commit, rootGetters },
        {
          type,
          showFreeSpaceModal,
          problemId
        }: {
          type: 'Run' | 'Judge'
          showFreeSpaceModal: boolean
          problemId: string
        }
      ) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const runningStr = isJapanese ? '実行中...' : 'Running...'
        const typeStr = isJapanese ? (type === 'Run' ? 'テスト' : 'ジャッジ') : type
        const id = showFreeSpaceModal ? 'FreeSpace' : problemId
        const arenaId = rootGetters[`${ArenaModuleName}/arenaId`]
        const arenaStr = arenaId == '' ? '' : `ArenaID: ${arenaId}<br>`

        const notification = await dispatch('createFormattedNotification', {
          title: runningStr,
          message: `${arenaStr}${typeStr}: ${id}`,
          type: 'info',
          icon: 'icon-info',
          position: 'top-right',
          duration: 0
        })

        commit('setRunningNotification', notification)
      },

      async noLoginNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'ログインしてください' : 'Please Login'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-right',
          duration: 2300
        })
      },

      async recommendEntryNotification({ dispatch, rootGetters }) {
        const isJapanese = rootGetters[`${ConfigStateModuleName}/displayLanguage`] == 'Japanese'
        const str = isJapanese ? 'レビューリクエストが出来ます' : 'You can enter a request'

        await dispatch('createFormattedNotification', {
          title: str,
          type: 'info',
          icon: 'icon-info',
          position: 'top-right',
          duration: 10000
        })
      },

      async closeRunningNotification({ commit }) {
        await commit('clearRunningNotification')
      }
    }
  }
}
